// Shared helpers for company-logo upload, normalization to PNG data URL,
// and rendering a logo into jsPDF documents.

export const MAX_LOGO_BYTES = 1.5 * 1024 * 1024; // 1.5 MB

export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Load any image data URL and rasterize it to a square PNG data URL of the
 * given pixel size. Preserves aspect ratio inside a transparent square canvas.
 */
export const normalizeLogoToPng = (dataUrl, size = 320) =>
  new Promise((resolve, reject) => {
    if (!dataUrl) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, size, size);
        const ratio = Math.min(size / img.width, size / img.height);
        const w = img.width * ratio;
        const h = img.height * ratio;
        const x = (size - w) / 2;
        const y = (size - h) / 2;
        ctx.drawImage(img, x, y, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });

export const handleLogoFileChange = async (event, setLogo) => {
  const file = event.target.files && event.target.files[0];
  if (!file) return;
  if (file.size > MAX_LOGO_BYTES) {
    alert('Logo file is too large. Please pick an image under 1.5 MB.');
    event.target.value = '';
    return;
  }
  try {
    const raw = await fileToDataUrl(file);
    const png = await normalizeLogoToPng(raw, 320);
    setLogo(png);
  } catch (err) {
    console.error('Failed to load logo', err);
    alert('Could not read that image. Please try another file.');
  } finally {
    event.target.value = '';
  }
};

/**
 * Draw a logo image (data URL) into a jsPDF doc at (x, y) sized to fit
 * `size` mm square. Returns the actual drawn width in mm.
 * Falls back to drawing a colored circle initial when no logo is present.
 */
export const drawPdfLogo = (doc, dataUrl, x, y, size, fallbackInitial = '', fallbackColor = [255, 255, 255]) => {
  if (dataUrl) {
    try {
      doc.addImage(dataUrl, 'PNG', x, y, size, size);
      return size;
    } catch (e) {
      // fall through to fallback
    }
  }
  const [r, g, b] = fallbackColor;
  doc.setFillColor(r, g, b);
  doc.circle(x + size / 2, y + size / 2, size / 2, 'F');
  if (fallbackInitial) {
    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size * 2);
    doc.text(fallbackInitial.charAt(0).toUpperCase(), x + size / 2, y + size / 1.5, { align: 'center' });
  }
  return size;
};
